import Whiteboard, { hasInk, legacyImageOf, strokesOf } from './Whiteboard.jsx';
import SketchView from './SketchView.jsx';

// A drawing page in the report's flow. In present mode it becomes a slide like
// every other section, which is the whole point: the advisor reaches the cash
// flow discussion, the next click IS the whiteboard, and no screen share
// changes hands.
//
// Which is exactly why it explains nothing. Everything on this page is on a
// screen the client is looking at, so "drawn live in the meeting" and a
// paragraph describing what the advisor is about to draw were stage directions
// delivered to the audience. The title and the drawing carry it.
//
// The ruling under a board is regenerated from the questionnaire, and the
// figures it was drawn from are saved with the ink. If the questionnaire has
// changed since, the board keeps the figures it was drawn on and says so,
// rather than the same strokes quietly landing on different numbers. Clearing
// the board is the deliberate act that brings today's figures back.

const sameFigures = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export default function WhiteboardSection({
  id,
  title,
  saved,
  onSave,
  readOnly,
  guideModel = null,
  renderGuide = null,
}) {
  // A client following the view link sees what was drawn for them, or nothing:
  // an empty drawing surface is advisor furniture, not deliverable.
  const drawn = hasInk(saved);
  if (readOnly && !drawn) return null;

  const legacy = legacyImageOf(saved);
  const savedGuide = saved && typeof saved === 'object' && saved.guide ? saved.guide : null;
  const inked = strokesOf(saved).length > 0;
  const stale = Boolean(inked && savedGuide && guideModel && !sameFigures(savedGuide, guideModel));
  const modelInUse = inked && savedGuide ? savedGuide : guideModel;
  // A legacy picture was drawn on a plain board; no ruling goes over it.
  const guide = renderGuide && modelInUse && !legacy ? renderGuide(modelInUse) : null;

  const note = legacy
    ? 'This board was drawn before boards were saved as strokes, so it is kept as a picture. '
      + 'It is too large to save to Salesforce and stays on this device. Clear it to start a board that saves.'
    : stale
      ? 'The questionnaire has changed since this was drawn. The board keeps the figures it was drawn on. '
        + 'Clear it to draw on today\'s figures.'
      : null;

  // An undrawn board is meeting furniture and never prints. A drawn one is
  // replayed for print rather than pasted in as a bitmap.
  return (
    <section className={`asmt-card asmt-boardcard${drawn ? '' : ' no-print'}`} id={id}>
      <div className="asmt-card-head">
        <h2>{title}</h2>
      </div>
      {readOnly
        ? <SketchView sketch={saved} label={title} className="asmt-board-saved" guide={guide} />
        : (
          <Whiteboard
            saved={saved}
            onSave={onSave}
            ariaLabel={title}
            guide={guide}
            background={legacy}
            saveWith={{ guide: modelInUse, legacy }}
            note={note}
          />
        )}
      {!readOnly && drawn && (
        <SketchView sketch={saved} label={title} className="asmt-board-print" guide={guide} />
      )}
    </section>
  );
}
